import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRewardsCatalogue, redeemReward, getLoyaltyBalance } from '../../api/loyalty';
import type { Reward as ApiReward, LoyaltyBalance as ApiLoyaltyBalance } from '../../api/loyalty';
import { PATHS } from '../../routes/routes';
import CustomerTopNav from '../../components/customerPortal/CustomerTopNav';
import CustomerFooter from '../../components/customerPortal/CustomerFooter';
import { format } from 'date-fns';
import '../../styles/customerPortal.css';

// import SecureStorage from '../../utils/secureStorage';

// Helper function to decode email from customer token
// function decodeEmailFromToken(bearerToken: string | null): string | null {
//     if (!bearerToken) return null;
//     const token = bearerToken.startsWith('Bearer ') ? bearerToken.slice(7) : bearerToken;
//     const parts = token.split('.');
//     if (parts.length < 2) return null;
//     try {
//         const payload = JSON.parse(atob(parts[1]));
//         const emailRaw = payload?.email ?? payload?.sub ?? null;
//         return emailRaw ? String(emailRaw).trim().toLowerCase() : null;
//     } catch {
//         return null;
//     }
// }

const RewardsCatalogue = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [pageError, setPageError] = useState('');
    const [inlineMessage, setInlineMessage] = useState('');
    const [success, setSuccess] = useState('');
    const [rewards, setRewards] = useState<ApiReward[]>([]);
    const [loyaltyBalance, setLoyaltyBalance] = useState<ApiLoyaltyBalance | null>(null);
    const [redeeming, setRedeeming] = useState<string | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedReward, setSelectedReward] = useState<ApiReward | null>(null);
    const [showModal, setShowModal] = useState(false);
    const itemsPerPage = 9;

    // Get customer email from token
    // const customerEmail = useMemo(() => decodeEmailFromToken(SecureStorage.getCustomerToken()), []);

    useEffect(() => {
        // Auth is enforced by CustomerGuard; just load data
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [rewardsData, balanceData] = await Promise.all([
                getRewardsCatalogue(),
                getLoyaltyBalance()
            ]);
            setRewards(rewardsData);
            setLoyaltyBalance(balanceData);
        } catch (error: any) {
            setPageError(error.response?.data?.error || 'Failed to load rewards catalogue');
        } finally {
            setLoading(false);
        }
    };

    const handleRedeemReward = async (reward: ApiReward) => {
        if (!loyaltyBalance || loyaltyBalance.availablePoints < reward.requiredPoints) {
            setInlineMessage('Insufficient points to redeem this reward');
            return;
        }

        if (reward.quantity <= 0) {
            setInlineMessage('This reward is currently out of stock');
            return;
        }

        try {
            setRedeeming(reward.id);
            setInlineMessage('');
            setSuccess('');

            const result = await redeemReward(reward.id);

            if (result.success) {
                setSuccess(`Successfully redeemed ${reward.title}! An email has been sent to you. Please check your email to continue.`);
                // Refresh data after successful redemption
                await fetchData();
                
                // Clear success message after 8 seconds (longer to read the message)
                setTimeout(() => setSuccess(''), 8000);
            }
        } catch (error: any) {
            const msg = error.response?.data?.error || 'Failed to redeem reward';
            // Show action feedback inline without switching to full-page error
            setInlineMessage(msg);
        } finally {
            setRedeeming(null);
        }
    };

    const categories: string[] = ['all', ...new Set(
        rewards
            .map(r => r.category)
            .filter((c): c is string => Boolean(c))
    )];

    const filteredRewards = (selectedCategory === 'all' 
        ? rewards 
        : rewards.filter(r => r.category === selectedCategory)
    ).filter(r => r.quantity > 0); // Only show rewards with available quantity

    useEffect(() => {
        // reset to page 1 when category changes
        setCurrentPage(1);
    }, [selectedCategory]);

    const paginatedRewards = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredRewards.slice(start, start + itemsPerPage);
    }, [filteredRewards, currentPage]);

    const totalPages = Math.max(1, Math.ceil(filteredRewards.length / itemsPerPage));

    const handleRewardClick = (reward: ApiReward) => {
        setSelectedReward(reward);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedReward(null);
    };

    // const PageHeader = ({ title, subtitle }: { title: string; subtitle?: string }) => (
    //     <div className="mb-6 text-left flex flex-col items-start w-full !text-left" style={{ textAlign: 'left', alignItems: 'flex-start' }}>
    //         <button
    //             onClick={() => navigate(-1)}
    //             className="self-start inline-flex items-center gap-2 px-3 py-1.5 rounded border border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700"
    //             style={{ alignSelf: 'flex-start' }}
    //         >
    //             <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    //                 <path d="M15 18l-6-6 6-6" />
    //             </svg>
    //             Back
    //         </button>
    //         <h1 className="self-start !text-left text-2xl sm:text-3xl font-bold mt-4 text-gray-900" style={{ textAlign: 'left', alignSelf: 'flex-start' }}>{title}</h1>
    //         {subtitle && <p className="self-start !text-left text-gray-500 mt-1" style={{ textAlign: 'left', alignSelf: 'flex-start' }}>{subtitle}</p>}
    //         <hr className="mt-4 border-gray-200" />
    //     </div>
    // );

    const PaginationLight = ({ page, total, onChange }: { page: number; total: number; onChange: (p: number) => void }) => {
        if (total <= 1) return null;
        const go = (p: number) => onChange(Math.min(total, Math.max(1, p)));
        const pages: number[] = [];
        for (let i = 1; i <= total; i++) pages.push(i);
        return (
            <div className="flex justify-center mt-8">
                <div className="join">
                    <button className="join-item btn btn-sm bg-white border border-gray-200 text-gray-700 hover:bg-gray-50" onClick={() => go(page - 1)} disabled={page === 1}>«</button>
                    {pages.map(p => (
                        <button
                            key={p}
                            className={`join-item btn btn-sm ${p === page ? 'bg-gray-100 text-gray-900 border border-gray-300' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                            onClick={() => go(p)}
                        >
                            {p}
                        </button>
                    ))}
                    <button className="join-item btn btn-sm bg-white border border-gray-200 text-gray-700 hover:bg-gray-50" onClick={() => go(page + 1)} disabled={page === total}>»</button>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="rewards-catalogue-page bg-gray-50 min-h-screen flex flex-col">
                <CustomerTopNav />
                <div className="flex-1 flex justify-center items-center p-6">
                    <div className="flex flex-col items-center gap-4">
                        <div className="loading loading-dots loading-lg"></div>
                        <p className="text-gray-500">Loading rewards catalogue...</p>
                    </div>
                </div>
                <CustomerFooter />
            </div>
        );
    }

    if (pageError) {
        return (
            <div className="rewards-catalogue-page bg-gray-50 min-h-screen flex flex-col">
                <CustomerTopNav />
                <div className="flex-1 flex justify-center items-center p-6">
                    <div className="text-center">
                        <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <p className="text-red-500 text-lg font-medium mb-2">Failed to load rewards</p>
                        <p className="text-gray-500 mb-4">{pageError}</p>
                        <button 
                            onClick={() => window.location.reload()}
                            className="px-4 py-2 bg-black text-white rounded hover:bg-gray-900 transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
                <CustomerFooter />
            </div>
        );
    }

    return (
        <div className="rewards-catalogue-page bg-gray-50 min-h-screen flex flex-col">
            {/* Header Section */}
            <CustomerTopNav />

            {/* Main Content */}
            <div className="p-6 flex-1">
                <div className="w-full">

                {/* Points Balance - Dashboard Style */}
                {loyaltyBalance && (
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-8">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-900">Your Points</h2>
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        </div>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-500 mb-2">Available for redemption</p>
                                <div className="text-3xl font-bold text-gray-900">
                                    {loyaltyBalance.availablePoints.toLocaleString()} points
                                </div>
                            </div>
                            <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                )}

                {/* Success/Error Messages */}
                {success && (
                    <div className="bg-green-50 border border-green-300 text-green-700 px-4 py-3 rounded mb-6">
                        {success}
                    </div>
                )}
                {inlineMessage && (
                    <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded mb-6">
                        {inlineMessage}
                    </div>
                )}

                {/* Category Filter - Dashboard Style */}
                {categories.length > 1 && (
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-8">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Filter by Category</h3>
                            <div className="w-2 h-2 rounded-full bg-gray-900"></div>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            {categories.map(category => (
                                <button
                                    key={category}
                                    onClick={() => setSelectedCategory(category)}
                                    className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                                        selectedCategory === category
                                            ? 'bg-black text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    {category === 'all' ? 'All Rewards' : category}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Rewards Grid - Dashboard Style */}
                {filteredRewards.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 shadow-sm border border-gray-200">
                        <div className="text-center">
                            <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">No rewards available</h3>
                            <p className="text-gray-500 mb-6">Check back later for new rewards and offers!</p>
                            <button
                                onClick={() => navigate(PATHS.CUSTOMER_PORTAL)}
                                className="px-6 py-3 bg-black text-white rounded hover:bg-gray-900 transition-colors"
                            >
                                Back to Portal
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {paginatedRewards.map(reward => (
                            <div
                                key={reward.id}
                                className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 overflow-hidden"
                            >
                                {/* Reward Image (with fallback) */}
                                <div 
                                    className="aspect-w-16 aspect-h-9 rounded-t-xl overflow-hidden bg-gray-100 flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity"
                                    onClick={() => handleRewardClick(reward)}
                                >
                                    {reward.imageUrl ? (
                                        <img
                                            src={reward.imageUrl}
                                            alt={reward.title}
                                            className="w-full h-32 object-cover"
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).style.display = 'none';
                                                const parent = (e.target as HTMLImageElement).parentElement;
                                                if (parent) parent.innerHTML = `\n<div class='w-full h-32 flex items-center justify-center bg-gray-100'>\n  <svg class='w-8 h-8 text-gray-400' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2'>\n    <path stroke-linecap='round' stroke-linejoin='round' d='M3 3v18h18'/>\n    <path stroke-linecap='round' stroke-linejoin='round' d='M8 13l3 3 5-5 5 5'/>\n  </svg>\n</div>`;
                                            }}
                                        />
                                    ) : (
                                        <div className="w-full h-32 flex items-center justify-center">
                                            <svg className="w-8 h-8 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v18h18"/>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M8 13l3 3 5-5 5 5"/>
                                            </svg>
                                        </div>
                                    )}
                                </div>

                                {/* Reward Content */}
                                <div className="p-6">
                                    <div className="flex items-start justify-between gap-2 mb-4">
                                        <h3 
                                            className="text-lg font-semibold text-gray-900 leading-tight cursor-pointer hover:text-gray-700 transition-colors"
                                            onClick={() => handleRewardClick(reward)}
                                        >
                                            {reward.title}
                                        </h3>
                                        {reward.category && (
                                            <span className="px-3 py-1 bg-gray-900 text-white text-xs font-medium rounded-full whitespace-nowrap">
                                                {reward.category}
                                            </span>
                                        )}
                                    </div>
                                    
                                    {/* <p className="text-gray-600 text-sm mb-6 line-clamp-3">
                                        {reward.description}
                                    </p> */}

                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 bg-gray-900 rounded flex items-center justify-center">
                                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                                    </svg>
                                                </div>
                                                <div className="text-left">
                                                    <div className="text-xl font-bold text-gray-900">
                                                        {reward.requiredPoints.toLocaleString()}
                                                    </div>
                                                    <div className="text-xs text-gray-500">points required</div>
                                                </div>
                                            </div>
                                            {/* <div className="text-right">
                                                <div className="text-sm text-gray-500">Available</div>
                                                <div className="text-sm font-semibold text-gray-900">
                                                    {reward.quantity}
                                                </div>
                                            </div> */}
                                        </div>
                                        
                                        <button
                                            onClick={() => handleRedeemReward(reward)}
                                            disabled={
                                                redeeming === reward.id || 
                                                !loyaltyBalance || 
                                                loyaltyBalance.availablePoints < reward.requiredPoints ||
                                                !reward.available ||
                                                reward.quantity <= 0
                                            }
                                            className={`w-full px-4 py-3 rounded text-sm font-medium transition-colors ${
                                                redeeming === reward.id
                                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                    : !loyaltyBalance || loyaltyBalance.availablePoints < reward.requiredPoints
                                                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                    : !reward.available
                                                    ? 'bg-red-100 text-red-500 cursor-not-allowed'
                                                    : 'bg-black hover:bg-gray-900 text-white'
                                            }`}
                                        >
                                            {redeeming === reward.id 
                                                ? 'Redeeming...'
                                                : !reward.available
                                                ? 'Out of Stock'
                                                : reward.quantity <= 0
                                                ? 'Out of Stock'
                                                : !loyaltyBalance || loyaltyBalance.availablePoints < reward.requiredPoints
                                                ? 'Insufficient Points'
                                                : 'Redeem Now'
                                            }
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                <PaginationLight page={currentPage} total={totalPages} onChange={setCurrentPage} />

                </div>
            </div>
            <CustomerFooter />

            {/* Reward Details Modal */}
            {showModal && selectedReward && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                    onClick={closeModal}
                >
                    <div 
                        className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="p-4 border-b border-gray-200">
                            <div className="flex items-start justify-between">
                                <div className="flex-1 text-left">
                                    <div className="flex items-center gap-3 mb-2">
                                        <h2 className="text-xl font-bold text-gray-900">{selectedReward.title}</h2>
                                        {selectedReward.category && (
                                            <span className="px-3 py-1 bg-gray-900 text-white text-sm font-medium rounded">
                                                {selectedReward.category}
                                            </span>
                                        )}
                                        <div className={`px-3 py-1 rounded-lg text-sm font-medium ${
                                            selectedReward.available && selectedReward.quantity > 0
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-red-100 text-red-800'
                                        }`}>
                                            {selectedReward.available && selectedReward.quantity > 0 ? 'Available' : 'Out of Stock'}
                                        </div>
                                        {!selectedReward.available && (
                                            <div className="px-3 py-1 bg-gray-100 text-gray-800 rounded-lg text-sm font-medium">
                                                Inactive
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={closeModal}
                                    className="ml-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div className="p-4">
                            {/* Reward Image */}
                            <div className="mb-4">
                                {selectedReward.imageUrl ? (
                                    <img
                                        src={selectedReward.imageUrl}
                                        alt={selectedReward.title}
                                        className="w-full h-40 object-cover rounded-lg"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).style.display = 'none';
                                            const parent = (e.target as HTMLImageElement).parentElement;
                                            if (parent) parent.innerHTML = `<div class='w-full h-40 flex items-center justify-center bg-gray-100 rounded-lg'><svg class='w-12 h-12 text-gray-400' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2'><path stroke-linecap='round' stroke-linejoin='round' d='M3 3v18h18'/><path stroke-linecap='round' stroke-linejoin='round' d='M8 13l3 3 5-5 5 5'/></svg></div>`;
                                        }}
                                    />
                                ) : (
                                    <div className="w-full h-40 flex items-center justify-center bg-gray-100 rounded-lg">
                                        <svg className="w-12 h-12 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v18h18"/>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 13l3 3 5-5 5 5"/>
                                        </svg>
                                    </div>
                                )}
                            </div>

                            {/* Description */}
                            <div className="mb-4 text-left">
                                <h3 className="text-base font-semibold text-gray-900 mb-2">Description</h3>
                                <p className="text-sm text-gray-600 leading-relaxed">{selectedReward.description}</p>
                            </div>

                            {/* Details Grid */}
                            <div className="grid grid-cols-2 gap-3 mb-4">
                                {/* Points Required */}
                                <div className="bg-gray-50 rounded-lg p-3 text-left">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-8 h-8 bg-gray-900 rounded-lg flex items-center justify-center">
                                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="text-lg font-bold text-gray-900 text-left">{selectedReward.requiredPoints.toLocaleString()}</div>
                                            <div className="text-xs text-gray-500">Points Required</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Available Quantity */}
                                 <div className="bg-gray-50 rounded-lg p-3 text-left">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="text-lg font-bold text-gray-900">{selectedReward.quantity}</div>
                                            <div className="text-xs text-gray-500">Available</div>
                                        </div>
                                    </div>
                                </div> 
                            </div>

                            {/* Validity Period */}
                            <div className="mb-4 text-left">
                                <h3 className="text-base font-semibold text-gray-900 mb-2">Validity Period</h3>
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                        <div className="text-left">
                                            <div className="text-xs text-gray-600">Valid until</div>
                                            <div className="text-sm font-semibold text-gray-900">
                                                {selectedReward.validUntil 
                                                    ? format(new Date(selectedReward.validUntil), 'MMM dd, yyyy - hh:mm a')
                                                    : 'No expiry date'
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            
                        </div>

                        {/* Modal Footer */}
                        <div className="p-4 border-t border-gray-200 bg-gray-50">
                            <div className="flex gap-2">
                                <button
                                    onClick={closeModal}
                                    className="flex-1 px-3 py-2 bg-gray-200 text-gray-800 font-medium rounded hover:bg-gray-300 transition-colors text-sm"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={() => {
                                        closeModal();
                                        handleRedeemReward(selectedReward);
                                    }}
                                    disabled={
                                        redeeming === selectedReward.id || 
                                        !loyaltyBalance || 
                                        loyaltyBalance.availablePoints < selectedReward.requiredPoints ||
                                        !selectedReward.available ||
                                        selectedReward.quantity <= 0
                                    }
                                    className={`flex-1 px-3 py-2 font-medium rounded transition-colors text-sm ${
                                        redeeming === selectedReward.id
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : !loyaltyBalance || loyaltyBalance.availablePoints < selectedReward.requiredPoints
                                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            : !selectedReward.available
                                            ? 'bg-red-100 text-red-500 cursor-not-allowed'
                                            : 'bg-black hover:bg-gray-900 text-white'
                                    }`}
                                >
                                    {redeeming === selectedReward.id 
                                        ? 'Redeeming...'
                                        : !selectedReward.available
                                        ? 'Out of Stock'
                                        : selectedReward.quantity <= 0
                                        ? 'Out of Stock'
                                        : !loyaltyBalance || loyaltyBalance.availablePoints < selectedReward.requiredPoints
                                        ? 'Insufficient Points'
                                        : 'Redeem Now'
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RewardsCatalogue;
